/**
 * Instagram API Service
 * 
 * Bu servis Instagram Basic Display API ile etkileşim sağlar.
 * Production'da gerçek API kullanılır, development'ta ise mock data döner.
 */

const INSTAGRAM_API_BASE = 'https://graph.instagram.com';

// Mock data - Development için
const mockInstagramPosts = [
  {
    id: '1',
    media_type: 'IMAGE',
    media_url: 'https://images.unsplash.com/photo-1519689680058-324335c77eba?w=800',
    permalink: 'https://www.instagram.com/minelakids_2025/',
    timestamp: new Date().toISOString(),
    caption: '✨ Yeni koleksiyonumuz sizlerle! #minelakids #cocukgiyim',
    like_count: 234,
    comments_count: 18
  },
  {
    id: '2',
    media_type: 'IMAGE',
    media_url: 'https://images.unsplash.com/photo-1503454537195-1dcabb73ffb9?w=800',
    permalink: 'https://www.instagram.com/minelakids_2025/',
    timestamp: new Date(Date.now() - 86400000).toISOString(),
    caption: '🎨 Renkli ve eğlenceli tasarımlarımız 🌈',
    like_count: 412,
    comments_count: 32
  },
  {
    id: '3',
    media_type: 'IMAGE',
    media_url: 'https://images.unsplash.com/photo-1522771739844-6a9f6d5f14af?w=800',
    permalink: 'https://www.instagram.com/minelakids_2025/',
    timestamp: new Date(Date.now() - 172800000).toISOString(),
    caption: '🧸 Oyuncak koleksiyonumuz çok seviliyor!',
    like_count: 189,
    comments_count: 15
  },
  {
    id: '4',
    media_type: 'IMAGE',
    media_url: 'https://images.unsplash.com/photo-1515488042361-ee00e0ddd4e4?w=800',
    permalink: 'https://www.instagram.com/minelakids_2025/',
    timestamp: new Date(Date.now() - 259200000).toISOString(),
    caption: '👗 İlkbahar koleksiyonu yolda! #fashion #kids',
    like_count: 567,
    comments_count: 45
  },
  {
    id: '5',
    media_type: 'IMAGE',
    media_url: 'https://images.unsplash.com/photo-1476958588088-f732246e3e85?w=800',
    permalink: 'https://www.instagram.com/minelakids_2025/',
    timestamp: new Date(Date.now() - 345600000).toISOString(),
    caption: '🌟 Kaliteli ve konforlu bebek kıyafetleri',
    like_count: 298,
    comments_count: 21
  },
  {
    id: '6',
    media_type: 'IMAGE',
    media_url: 'https://images.unsplash.com/photo-1514090458221-65bb69cf63e6?w=800',
    permalink: 'https://www.instagram.com/minelakids_2025/',
    timestamp: new Date(Date.now() - 432000000).toISOString(),
    caption: '❤️ Sevgiyle hazırlanan ürünlerimiz #minelakids',
    like_count: 345,
    comments_count: 28
  }
];

/**
 * Instagram postlarını getirir
 * @param {number} limit - Maksimum post sayısı
 * @returns {Promise<Array>} Instagram postları
 */
export const getInstagramPosts = async (limit = 6) => {
  // Eğer environment'ta Instagram token varsa gerçek API'yi kullan
  const accessToken = process.env.REACT_APP_INSTAGRAM_ACCESS_TOKEN;
  const userId = process.env.REACT_APP_INSTAGRAM_USER_ID;

  if (accessToken && userId) {
    try {
      const fields = 'id,caption,media_type,media_url,thumbnail_url,permalink,timestamp,like_count,comments_count';
      const response = await fetch(
        `${INSTAGRAM_API_BASE}/${userId}/media?fields=${fields}&access_token=${accessToken}&limit=${limit}`
      );

      if (!response.ok) {
        throw new Error(`Instagram API error: ${response.status}`);
      }

      const data = await response.json();
      
      // Sadece IMAGE ve CAROUSEL_ALBUM tiplerini döndür (VIDEO hariç)
      return data.data
        .filter(post => post.media_type === 'IMAGE' || post.media_type === 'CAROUSEL_ALBUM')
        .map(post => ({
          id: post.id,
          image: post.media_url,
          likes: post.like_count || 0,
          comments: post.comments_count || 0,
          caption: post.caption || '',
          permalink: post.permalink,
          timestamp: post.timestamp
        }));
    } catch (error) {
      console.warn('Instagram API hatası, mock data kullanılıyor:', error);
      return getMockPosts(limit);
    }
  }

  // Token yoksa mock data döndür
  return getMockPosts(limit);
};

/**
 * Mock Instagram postlarını döndürür
 * @param {number} limit - Maksimum post sayısı
 * @returns {Array} Mock Instagram postları
 */
const getMockPosts = (limit = 6) => {
  return mockInstagramPosts.slice(0, limit).map(post => ({
    id: post.id,
    image: post.media_url,
    likes: post.like_count,
    comments: post.comments_count,
    caption: post.caption,
    permalink: post.permalink,
    timestamp: post.timestamp
  }));
};

/**
 * Instagram token'ını yeniler (Backend'de yapılmalı)
 * @param {string} currentToken - Mevcut access token
 * @returns {Promise<string>} Yeni access token
 */
export const refreshInstagramToken = async (currentToken) => {
  try {
    const response = await fetch(
      `${INSTAGRAM_API_BASE}/refresh_access_token?grant_type=ig_refresh_token&access_token=${currentToken}`
    );

    if (!response.ok) {
      throw new Error('Token yenileme başarısız');
    }

    const data = await response.json();
    return data.access_token;
  } catch (error) {
    console.error('Instagram token yenileme hatası:', error);
    throw error;
  }
};

export default {
  getInstagramPosts,
  refreshInstagramToken
};
